/* **A KANON klienci v2 screen's shell** — the bounded page, and the measure that centres inside it.
 *
 * A stylesheet with no component, like `photoBox.module.css` and `ghostButton.module.css` beside
 * it: there is nothing behavioural here, only two boxes, and a component wrapping them would put a
 * React boundary around a `<main>` and a `<div>` for nothing.
 *
 * Promoted on 2 September 2026 on its second consumer. `pages/client-roster` (screen 09) and
 * `pages/client-archive` (screen 10) had these seventeen declarations written out byte for byte in
 * both, which is not a coincidence to be tidied: it is v2's own rule, stated in every one of its
 * files, and the next v2 screen would have transcribed it a third time.
 *
 * ── The MEASURE — v2's rule, not a preference ────────────────────────────────────────────────
 * v1 drew a screen's chrome as one `--bg-card` surface running to both edges of the frame. v2's
 * instruction, written into every file and again into the hub's note: **content keeps its
 * proportions and does not fill the monitor by force.** It holds a measure of about 1600px and
 * centres in it, so on a 27–32" display the surplus goes to the margins rather than into stretched
 * columns. The KANON preview renders full-panel-width on purpose and says so in its own caption —
 * "nie przenoś tego zachowania do implementacji".
 *
 * ── The page is BOUNDED to the viewport, and content scrolls INSIDE it ───────────────────────
 * A desk tool keeps its chrome. With the document scrolling instead, a roster's column headers and
 * its whole toolbar leave the screen at row ten, and narrowing a forty-client list would mean
 * scrolling back to the top to reach the search box. `position: sticky` cannot rescue that: a
 * table's own scroller is already the nearest scrollport for anything sticky inside it, so `top: 0`
 * against a scrolling document resolves to nothing.
 *
 * `flex: none` is what makes the bound real. With `flex: 1` a flex item's `flex-basis: 0%` replaces
 * `height` on the main axis, the page stretches to the shell's work column, the column grows with
 * its content, and the document scrolls exactly as before.
 *
 * `min-height: 0` on the measure is the same rule one level down: a flex item's default
 * `min-height: auto` refuses to shrink below its content, so without it the measure grows to the
 * height of the longest list and the page scrolls instead of the table.
 *
 * `100dvh` rather than `100vh`, for the reason `AppShell` uses it — a laptop's collapsing chrome.
 *
 * ── What a screen still declares for itself ──────────────────────────────────────────────────
 * Everything inside: its cards, its panes, its tables, and any width query of its own. Nothing may
 * be declared on both sides — a declaration living in a shared module and a page's own is settled
 * by CHUNK ORDER, which differs between the dev server and the build. The argument is written out
 * at length in `emberButton.module.css`. */

.page {
  display: flex;
  flex: none;
  flex-direction: column;
  min-width: 0;
  height: 100dvh;
  overflow: hidden;
  background: var(--bg);
}

/* A screen with a panel laid over it (`shared/lib/useInPlaceSession`): the overlay's box is
 * `position: absolute; inset: 0`, and this is what it is measured against — only while it is open,
 * so no absolutely placed descendant of a resting screen changes its containing block. */
.pageHosting {
  position: relative;
}

.measure {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
  max-width: var(--w-canon-measure);
  min-height: 0;
  margin: var(--space-0) auto;
  padding: var(--space-7) var(--space-30px) var(--space-6);
  overflow: hidden;
}
